import { Button } from '../../ui/button/button';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { push } from 'connected-react-router';
import { RoutingPath } from '../../../routes/routing-path';
import { Input } from '../../ui/input/input';
import { LogDecorator } from '@tenryu/log-decorator';
import './login.scss';
import { apiActions } from '../../../slices/api-slice';
import loginBg from '../../../assets/img/bg/login_bg.jpg';
import loginLogo from '../../../assets/img/logo/ppmcloud_03_t.png';
import { dialogActions } from '../../dialog/slice/dialog-slice';
import { ResponseBase } from '../../../api/response-base';
import iconEyeOn from '../../../assets/img/icon/eye_on.svg';
import iconEyeOff from '../../../assets/img/icon/eye_off.svg';
import chromeIcon from '../../../assets/img/icon/googlechrome_icon.svg';
import { commonActions } from '../../../slices/common-slice';
import { ApiBaseBack } from '../../../api/back/api-base-back';
import { SessionTokenManager } from '../../../manager/session-token-manager';
import { ApiBaseFront } from '../../../api/front/api-base-front';
import { TokenPostForStaffResponse } from '../../../api/front/token/api-token';
import { localStorageActions } from '../../../slices/local-storage-slice';
import { useAppSelector } from '../../../app/hooks';
import lodash from 'lodash';
import { ForgetPasswordDialog } from '../../ui/forget-password-dialog/forget-password-dialog';
import { Env } from '../../../models/env';
import { STAFF_AUTH } from '../../../collection/staff-auth-collection';
import { RECAPTCHA_ACTIONS, useRecaptcha } from '../../../hooks/recaptcha';
import ReCAPTCHA from 'react-google-recaptcha';
import { RecaptchaSkipTokenManager } from '../../../manager/recaptcha-skip-token-manager';
import { useEnsureTermsAgreement } from '../../../hooks/use-terms-agreement';
import { webPagePath } from '../../../models/web-page-path';
import { recaptchaActions } from '../../../slices/recaptcha-slice';
import { validatePassword } from '../../../utilities/validation/password';

// - ツールチップ表示するもの -
type InputErrorItem = 'store' | 'password' | 'staffId' | 'none';
const AUTH_INPUT_ERROR_MESSAGE = '入力された情報のいずれかが正しくありません';

export const StaffLogin = () => {

  // - 半角数値正規表現 -
  const IntegerRegExp = useRef(/^[0-9]+/).current;
  // - 半角英数字記号正規表現 -
  const halfWidthStringExp = useRef(/^[!-~]+$/).current;
// - バリデーションチェック -
  const checkInputValidation = useCallback((type: InputErrorItem, inputItem: string): boolean  => {
    let result: boolean;
    switch (type) {
      case 'store':
        result = !!(inputItem && inputItem.match(IntegerRegExp) && inputItem.length === 7);
        break;
      case 'staffId':
        result = !!(inputItem && (inputItem.match(halfWidthStringExp) && inputItem.length >= 6));
        break;
      case 'password':
        result = !!(inputItem && validatePassword(inputItem));
        break;
      default:
        result = true;
        break;
    }
    return result;
  }, []);

  // - Effect -
  // -- ログまとめ --
  useEffect(() => {
    LogDecorator.group('<red>ログイン</red>');
    return () => LogDecorator.groupEnd();
  }, []);
  const { kijshopCdInitialValue, dialogs, isChrome, loginMode, isRedirect } = useAppSelector((state) => ({
    kijshopCdInitialValue: state.storage.staffInfo.kijshopCd,
    dialogs: state.dialog.dialogs,
    isChrome: state.common.isChrome,
    loginMode: state.storage.loginMode,
    isRedirect: state.common.isRedirect,
  }), lodash.isEqual);
  // - Hooks -
  const dispatch = useDispatch();
  const { v3, v2 } = useRecaptcha();
  const { ensureTermsAgreed } = useEnsureTermsAgreement();
  // - state -
  // -- 店舗ID --
  const [store, setStore] = useState(kijshopCdInitialValue);
  // -- スタッフID --
  const [staffId, setStaffId] = useState('');
  // -- パスワード --
  const [password, setPassword] = useState('');
  // -- パスワード表示有無 --
  const [isDisplayPass, setIsDisplayPass] = useState(false);
  // -- xmlバージョンリクエスト情報 --
  const [xmlVersion, setXmlVersion] = useState('');
  const [xml, setXml] = useState('');
  const [version, setVersion] = useState('');
  // -- 店舗コードが未入力か --
  const [isStoreEmpty, setIsStoreEmpty] = useState(false);
  // -- スタッフIDが未入力か --
  const [isStaffIdEmpty, setIsStaffIdEmpty] = useState(false);
  // -- パスワードが未入力か --
  const [isPasswordEmpty, setIsPasswordEmpty] = useState(false);
  // 認証入力（店舗コード・スタッフID・パスワード）に不備があるか
  const [hasAuthInputError, setHasAuthInputError] = useState(false);
  // -- ツールチップ表示するもの --
  const [tooltipItem, setTooltipItem] = useState<InputErrorItem>('none');
  // - Callback -
  // -- 店舗ID --
  const handlerChangeStore = useCallback((v) => {
    setStore(v);
  }, []);
  // -- スタッフID --
  const handlerChangeStaffId = useCallback((v) => {
    setStaffId(v);
  }, []);
  // -- パスワード --
  const handlerChangePassword = useCallback((v) => {
    setPassword(v);
  }, []);

// -- ログイン --
  const callLoginApi = useCallback(() => {
    dispatch(apiActions.staffLogin(
      {
        kijshopCd: store,
        staffId,
        password,
      },
      {
        onSuccess: (res: ResponseBase<TokenPostForStaffResponse>) => {
          if (!res.body.data) return;
          if (res.body.data.authority === STAFF_AUTH.GUST) {
            dispatch(dialogActions.pushMessage({
              title: '確認',
              message: [
                'PPMクラウドへのログインが許可されていません。',
                'アカウントの権限を店舗の管理者にご確認ください。'
              ],
              buttons: [
                {
                  label: 'OK',
                  callback: () => {
                    console.log('you are gust !!');
                    dispatch(dialogActions.pop());
                  },
                },
              ],
            }));
            return;
          }
          if (res.body.data?.needRecaptchaV2) {
            v2.triggerV2();
            return;
          }
          const sessionToken = res.body.data.sessionId || '';
          const fSessionToken = res.body.data.token || '';
          ApiBaseBack.session = sessionToken;
          ApiBaseFront.session = fSessionToken;
          new SessionTokenManager().save(sessionToken, fSessionToken);
          const tmpToken = res.body.data.tmpRecaptchaToken || '';
          ApiBaseFront.tmpRecaptcha = tmpToken;
          new RecaptchaSkipTokenManager().save(tmpToken);
          dispatch(recaptchaActions.setNeedV2(false));
          // dispatch(commonActions.setKijshopCd(res.body.data?.[0]?.kijshopCd || ''));
          dispatch(commonActions.getData(store));
          dispatch(localStorageActions.getLocalData(store));
          dispatch(localStorageActions.setData(store, 'shopName', res.body.data?.shopName || ''));
          dispatch(localStorageActions.setData(store, 'isAdmin', Boolean(res.body.data?.isAdmin) || false));
          dispatch(localStorageActions.setLoginMode('STAFF'));
          dispatch(apiActions.versionCheck(
            {
              xmlVersion,
              xml,
              version,
            },
            {
              onSuccess: () => {
                if (!res.body.data) return;
                if (!res.body.data.status) {
                  dispatch(push(RoutingPath.cnv.staffPasswordRegister({ kijshopCd: store })));
                  return;
                }
                dispatch(commonActions.getData(store));
                dispatch(localStorageActions.getLocalData(store));
                dispatch(localStorageActions.setMachineId(staffId));
                ensureTermsAgreed(() => {
                  dispatch(push(RoutingPath.cnv.orders({ kijshopCd: store })));
                });
              },
            },
          ));
        },
        onBasicError: (e) => {
          const hasError =
            !checkInputValidation('store', store) || !checkInputValidation('staffId', staffId) || !checkInputValidation('password', password);
          setHasAuthInputError(hasError);
          dispatch(dialogActions.pushMessage({
            title: '確認',
            message: e.messages,
            buttons: [
              {
                label: 'OK',
                callback: () => {
                  dispatch(dialogActions.pop());
                },
              },
            ],
          }));
        },
      },
    ));
  }, [
    store,
    password,
    staffId,
    checkInputValidation,
    v2,
    ensureTermsAgreed,
  ]);

  const handlerClickLogin = useCallback(() => {
    console.log('login check...');
    const storeEmpty = !store;
    const passwordEmpty = !password;
    const staffIdEmpty = !staffId;
    setIsStoreEmpty(storeEmpty);
    setIsPasswordEmpty(passwordEmpty);
    setIsStaffIdEmpty(staffIdEmpty);
    if (storeEmpty || passwordEmpty || staffIdEmpty) {
      console.log('login success!!');
      return;
    }
    if (v2.needV2) {
      v2.triggerV2();
    } else {
      v3.getToken(RECAPTCHA_ACTIONS.STAFF_LOGIN, callLoginApi);
    }
  }, [
    v3,
    v2,
    callLoginApi,
  ]);

  // -- パスワード表示有無 --
  const handlerClickDisplayPassword = useCallback(() => {
    if (!isChrome) return;
    setIsDisplayPass(prev => !prev);
  }, [isDisplayPass, isChrome]);
  const handlerClickForgetPass = useCallback(() => {
    dispatch(dialogActions.push({
      title: 'パスワードの種類を選択してください',
      element: <ForgetPasswordDialog />,
    }));
  }, []);
  const handlerClickAdminLogin = useCallback(() => {
    if (isChrome) {
      dispatch(push(RoutingPath.login));
    }
  }, [isChrome]);
  const handlerMouseOver = useCallback((type: InputErrorItem) => {
    setTooltipItem(type);
  }, []);

  useEffect(() => {
    if (!dialogs.length && (ApiBaseBack.session || ApiBaseFront.session)) {
      new SessionTokenManager().save('', '');
      window.location.reload();
    }
    dispatch(commonActions.checkBrowser());
    // MEMO 9/8のMTGでリダイレクトはしないことになったのでコメントアウト
    // if (!isRedirect) {
    //   if (loginMode === 'ADMIN') {
    //     dispatch(push(RoutingPath.login));
    //   } else {
    //     dispatch(push(RoutingPath.staffLogin));
    //   }
    //   dispatch(commonActions.setIsRedirect(true));
    // }
    // new SessionTokenManager().save('sessionToken', fSessionToken);
  }, []);


  return (
    <div className="login">
      <div className="login__inner">
        <div className="login__col2">
          <div className="login__col2__inner">
            <h1 className="login__col2__inner__header">
              スタッフIDでログイン
            </h1>
            <div className="login__col2__inner__contents">
              <Input
                value={store}
                onChange={(e) => handlerChangeStore(e.target.value)}
                handlerEnter={handlerClickLogin}
                placeholder="店舗ID"
                uiSize="lg"
                maxLength={7}
                onMouseOver={() => handlerMouseOver('store')}
                onMouseLeave={() => handlerMouseOver('none')}
                error={isStoreEmpty || hasAuthInputError}
                tooltip={
                  tooltipItem === 'store'
                    ? isStoreEmpty
                      ? { messages: ['店舗IDが未入力です'] }
                      : hasAuthInputError
                        ? { messages: [AUTH_INPUT_ERROR_MESSAGE] }
                        : undefined
                    : undefined
                }
                disabled={!isChrome}
              />
              <Input
                value={staffId}
                onChange={(e) => {
                  handlerChangeStaffId(e.target.value);
                }}
                placeholder="スタッフID"
                uiSize="lg"
                onMouseOver={() => handlerMouseOver('staffId')}
                onMouseLeave={() => handlerMouseOver('none')}
                error={isStaffIdEmpty || hasAuthInputError}
                tooltip={
                  tooltipItem === 'staffId'
                    ? isStaffIdEmpty
                      ? { messages: ['スタッフIDが未入力です'] }
                      : hasAuthInputError
                        ? { messages: [AUTH_INPUT_ERROR_MESSAGE] }
                        : undefined
                    : undefined
                }
                disabled={!isChrome}
              />
              <Input
                type={isDisplayPass ? '' : 'password'}
                value={password}
                onChange={(e) => {
                  handlerChangePassword(e.target.value);
                }}
                handlerEnter={handlerClickLogin}
                placeholder="パスワード"
                uiSize="lg"
                maxLength={15}
                onMouseOver={() => handlerMouseOver('password')}
                onMouseLeave={() => handlerMouseOver('none')}
                error={isPasswordEmpty || hasAuthInputError}
                tooltip={
                  tooltipItem === 'password'
                    ? isPasswordEmpty
                      ? { messages: ['パスワードが未入力です'] }
                      : hasAuthInputError
                        ? { messages: [AUTH_INPUT_ERROR_MESSAGE] }
                        : undefined
                    : undefined
                }
              />
              <div
                className="login__pass_display"
                onClick={handlerClickDisplayPassword}
              >
                <img
                  src={isDisplayPass ? iconEyeOff : iconEyeOn}
                  alt=""
                />
                <span>{isDisplayPass ? 'パスワードを非表示にする' : 'パスワードを表示する'}</span>
              </div>
              <Button
                label="ログイン"
                onClick={handlerClickLogin}
                // disabled={!store || !password}
                disabled={!isChrome}
                color="blue_cyan"
                size="lg"
              />
              <p
                className="login__pass_remember"
                onClick={handlerClickForgetPass}
              >
                パスワードをお忘れの方はこちら
              </p>
            </div>
            <div className={'chrome__guide'}>
              <p>● PPMクラウドの推奨利用環境について</p>
              <p>&emsp;・動作対象ブラウザは【 Google Chrome 】です</p>
              <p>&emsp;&emsp;それ以外のブラウザでは、ご利用いただけません</p>
              <div className={'chrome__guide__link'}>
                <p>{'>'}&emsp;
                  <a href="https://support.google.com/chrome/answer/95346?hl=ja&co=GENIE.Platform%3DDesktop#zippy=">
                    Google Chromeの導入については、こちら
                  </a>
                </p>
                <img
                  src={chromeIcon}
                  alt=""
                  width={40}
                  height={40}
                />
              </div>
              <p className="chrome__guide__recaptcha">
                このサイトは reCAPTCHA によって保護されており、Google の
                <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">
                  プライバシーポリシー
                </a>
                と
                <a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer">
                  利用規約
                </a>
                が適用されます。
              </p>
            </div>
            <div className={'version version__staff'}>{Env.version ? `ver${Env.version}` : ''}</div>
          </div>
        </div>
        <div className="login__col1">
          <img
            src={loginBg}
            alt=""
            style={{ height: '100%', width: '100%', objectFit: 'cover' }}
          /> {/* リロード時対策でインライン指定 */}
          <img
            src={loginLogo}
            alt=""
          />
          <div className={'switch_login'}>
            <Button
              color={'warning'}
              label="店舗IDでログインする"
              onClick={handlerClickAdminLogin}
              size={'lg'}
            />
          </div>
          <div className='login__col1__footer'>
            <span className='copywriter'>&copy; LABONETWORK Co.,Ltd.</span>
            <span className="separator">｜</span>
            <a
              className="terms-link"
              href={webPagePath.termsOfService}
              target="_blank"
              rel="noopener noreferrer"
            >
              利用規約
            </a>
          </div>
        </div>
      </div>
      {v2.showV2 && (
        <div className="recaptcha-modal">
          <div className="recaptcha-modal__backdrop" />
          <div className="recaptcha-modal__content">
            <ReCAPTCHA
              ref={v2.v2Ref}
              sitekey={Env.recaptcha.v2SiteKey}
              onChange={(token) => v2.onV2Change(token, callLoginApi)}
            />
          </div>
        </div>
      )}
    </div>
  );
};
